import { useMemo, useState } from "react"
import type { ColumnDef, PaginationState } from "@tanstack/react-table"
import { ChevronDown } from "lucide-react"

import { DataTable } from "@/components/DataTable"
import { UsersTable } from "@/components/subject/UsersTable"
import { createUserTableRows } from "@/components/subject/userTableData"
import type { OtkSubjectResponsiblePerson } from "@/lib/api/subjects"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { permissionLabels, permissionOrder } from "@/lib/permissions"
import {
  deleteOtkUser,
  type OtkPermission,
  type OtkRole,
  type OtkUser,
  type Role,
} from "@/lib/api/users"
import { useIsAdmin, useRolePermissions, useSession } from "@/store/session"

// Stable fallback reference — `subject?.responsiblePersons ?? []` would
// otherwise hand `RoleCard`'s `rows` useMemo a new array every render
// whenever `subject` is still `null`, defeating the memoization (see its
// own comment).
const NO_RESPONSIBLE_PERSONS: OtkSubjectResponsiblePerson[] = []

function RoleCard({
  role,
  roleRows,
  users,
  roleLabelById,
  canManageUsers,
  isAdmin,
  onDeleteUser,
  onEditUser,
}: {
  role: OtkRole
  roleRows: OtkRole[]
  users: OtkUser[]
  roleLabelById: Record<string, string>
  canManageUsers: boolean
  isAdmin: boolean
  onDeleteUser: (id: string) => void | Promise<void>
  onEditUser: (id: string, roleId: Role) => void
}) {
  const subject = useSession((s) => s.subject)
  const responsiblePersons =
    subject?.responsiblePersons ?? NO_RESPONSIBLE_PERSONS
  // subject-roles.md RO1: role-card membership is filtered from the real
  // `roles[]` array, not a single flat `role` field the prototype's mock
  // model used — a real account can hold more than one role, so it can
  // legitimately appear under more than one card.
  const roleUsers = useMemo(
    () => users.filter((user) => user.roles.includes(role.id)),
    [users, role.id]
  )
  const rows = useMemo(
    () => createUserTableRows(roleUsers, responsiblePersons, roleLabelById),
    [roleUsers, responsiblePersons, roleLabelById]
  )

  return (
    <Collapsible className="group/role-card">
      <div className="overflow-hidden rounded-xl border bg-card text-card-foreground shadow-sm">
        <CollapsibleTrigger asChild>
          <button
            className="group/trigger flex w-full items-center justify-between gap-4 px-4 py-4 text-left hover:bg-muted/40"
            type="button"
          >
            <span className="font-medium">{role.name}</span>
            <span className="flex shrink-0 items-center gap-3 text-sm text-muted-foreground">
              <span>Користувачів: {roleUsers.length}</span>
              <ChevronDown className="size-4 shrink-0 text-muted-foreground transition-transform group-hover/trigger:text-foreground group-data-[state=open]/role-card:rotate-180" />
            </span>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t">
            <UsersTable
              canManageUsers={canManageUsers}
              data={rows}
              includeRole={false}
              isAdmin={isAdmin}
              onDeleteUser={onDeleteUser}
              onEditUser={onEditUser}
              roleRows={roleRows}
            />
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}

/**
 * subject-roles.md's `RolesPermissionsCard`. Row actions inside each role
 * card's embedded `UsersTable` follow the same admin gate as `/subject/users`
 * (subject-roles.md's Access/Interactions): assigning or changing a user's
 * role reuses `EditUserDialog` (which already replays the real
 * `/add-role`/`remove-role` calls), and removing a user reuses
 * `DestructiveUserAction`. Only the permission matrix below stays read-only.
 *
 * Like the wireframe's own version, this card owns a **separate** local copy
 * of the user list (seeded from the `users` prop) rather than sharing
 * `/subject/users`' state — deleting a user calls the real
 * `POST /user/:id/delete` (`deleteOtkUser`) before removing the row from
 * this card's own local state.
 */
export function RolesPermissionsCard({
  roleRows,
  users,
  roleLabelById,
}: {
  roleRows: OtkRole[]
  users: OtkUser[]
  roleLabelById: Record<string, string>
}) {
  const canManageUsers = useRolePermissions().manageUsersRolesInvites
  const isAdmin = useIsAdmin()
  // Lazy-seeded once, like `SubjectUsers.tsx`'s own local `tableRows` state —
  // `SubjectRoles.tsx` only renders this component after its fetch resolves,
  // so `users` never changes under an already-mounted instance.
  const [localUsers, setLocalUsers] = useState<OtkUser[]>(users)
  const [permissionsPagination, setPermissionsPagination] =
    useState<PaginationState>({
      pageIndex: 0,
      pageSize: permissionOrder.length,
    })

  const removeUser = (id: string) => {
    setLocalUsers((current) => current.filter((user) => user.id !== id))
  }

  const deleteUser = async (id: string) => {
    await deleteOtkUser(id)
    removeUser(id)
  }

  // Mirrors `SubjectUsers.tsx`'s own `editUser`: a real role change has
  // already been applied server-side by `EditUserDialog.handleSave` before
  // this fires, so this only keeps local `localUsers` in sync — which is
  // what moves the row from this role's card to the newly selected one.
  const editUser = (id: string, roleId: Role) => {
    setLocalUsers((current) =>
      current.map((user) =>
        user.id === id ? { ...user, roles: [roleId] } : user
      )
    )
  }

  const permissionColumns = useMemo<ColumnDef<OtkPermission>[]>(
    () => [
      {
        id: "permission",
        header: "Право",
        cell: ({ row }) => permissionLabels[row.original],
      },
      // Права лише показуються — Checkbox завжди disabled, це індикатор
      // стану, а не контрол (design-system.md / subject-roles.md's Design).
      ...roleRows.map((role) => ({
        id: role.id,
        header: role.name,
        meta: { className: "w-72" },
        cell: ({ row }: { row: { original: OtkPermission } }) => (
          <Checkbox checked={role.permissions[row.original]} disabled />
        ),
      })),
    ],
    [roleRows]
  )

  return (
    <div className="mt-4 space-y-6">
      {isAdmin && (
        <section className="space-y-3">
          <div>
            <h2 className="text-lg font-semibold">Ролі в АРМ</h2>
            <p className="text-sm text-muted-foreground">
              Лише перегляд. Права застосовуються в межах пунктів ОТК,
              призначених користувачу.
            </p>
          </div>
          {roleRows.map((role) => (
            <RoleCard
              canManageUsers={canManageUsers}
              isAdmin={isAdmin}
              key={role.id}
              onDeleteUser={deleteUser}
              onEditUser={editUser}
              role={role}
              roleLabelById={roleLabelById}
              roleRows={roleRows}
              users={localUsers}
            />
          ))}
        </section>
      )}
      <Card>
        <CardHeader className="flex-row items-start justify-between">
          <div>
            <CardTitle>Права доступу</CardTitle>
            <CardDescription>
              Права ролей визначені наперед і доступні лише для перегляду.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={permissionColumns}
            data={permissionOrder}
            onPaginationChange={setPermissionsPagination}
            pagination={permissionsPagination}
            tableClassName="table-fixed"
          />
          <p className="mt-4 text-sm text-muted-foreground">
            Перелік ролей в АРМ і їхні права визначені наперед і не
            редагуються.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
